import {
    Modal,
    Box,
    Button,
    Typography,
    IconButton,
    debounce,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close' // Import the close icon
import { ISelectorForRelations } from '../../../../services/apiInterfaces'
import { IAssetSensorMarker } from '../interfaces'
import { ReactElement, useCallback, useState } from 'react'
import useGetAvailableRelatedEntities from '../../../../CustomHooks/EntityInstances/getAvailableRelatedEntities'
import { useAppSelector } from '../../../../redux/hook'
import theme from '../../../../theme/theme'
import TruncatedText from '../../../Common/TruncatedText'
import SearchInput from '../../../Common/SearchInput'
import { useTranslation } from 'react-i18next'

interface ISensorsDropDownModal {
    showSensorsDropdownModal: boolean
    onClose: () => void
    handleSubmit: (selectedSensor?: ISelectorForRelations) => void
    droppedAsset: IAssetSensorMarker | null
}

const SensorsDropDownModal = (props: ISensorsDropDownModal): ReactElement => {
    const { t } = useTranslation()
    const { showSensorsDropdownModal, onClose, handleSubmit, droppedAsset } =
        props
    const { assetName = '' } = droppedAsset || ({} as IAssetSensorMarker)
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const {
        assetEntityTemplateId,
        sensorEntityTemplateId,
        presentConfiguringAssetSensorMarkers,
    } = fmSlice
    const org = useAppSelector((state) => state.org.org)
    const [searchText, setSearchText] = useState('')
    const [debouncedSearchText, setDebouncedSearchText] = useState('')
    const [selectedSensor, setSelectedSensor] =
        useState<ISelectorForRelations | null>(null)
    const [enableAPICall, setEnableAPICall] = useState<boolean>(true)
    const includeAttributes = true

    const {
        data: availableSensors = [],
        // isError: isLoadingAvailableSensorsError,
        // isFetching: isFetchingAvailableSensors,
        // isLoading: isLoadingAvailableSensors,
    } = useGetAvailableRelatedEntities(
        org?.id || '',
        sensorEntityTemplateId,
        assetEntityTemplateId,
        debouncedSearchText,
        includeAttributes,
        enableAPICall,
    )

    const freeSensors = availableSensors.filter(
        (sensor) =>
            presentConfiguringAssetSensorMarkers.findIndex(
                (sensorMarker) => sensorMarker.sensorId === sensor.entityId,
            ) === -1,
    )
    const debouncedSearch = useCallback(
        // Wrap with useCallback
        debounce((value) => {
            setDebouncedSearchText(value)
            setEnableAPICall(true)
        }, 400),
        [],
    )

    const onSearch = (id: string, value: string) => {
        setSearchText(value)
        debouncedSearch(value)
    }

    const onSelectSensor = (sensor: ISelectorForRelations) => {
        setSearchText(sensor.displayName)
        setEnableAPICall(false)
        setSelectedSensor(sensor)
    }

    const onSubmitSensor = () => {
        if (selectedSensor) {
            handleSubmit(selectedSensor)
        }
    }

    const disableSubmit =
        !selectedSensor || selectedSensor.displayName !== searchText

    return (
        <Modal
            id="fm-sensors-modal"
            open={showSensorsDropdownModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box
                sx={{
                    p: 4,
                    boxShadow: 4,
                    borderRadius: 4,
                    position: 'relative',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    backgroundColor: '#fff',
                    border: `2px solid ${theme.palette.primary.main}`,
                }}
            >
                <IconButton
                    id="fm-sensors-button-close"
                    aria-label={t('close') || 'Close'}
                    title={t('close') || 'Close'}
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500], // Customize icon color
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box
                    sx={{
                        display: 'flex',
                        whiteSpace: 'nowrap',
                    }}
                    mb={2}
                    id="selected-sensor"
                >
                    <Typography
                        id="modal-modal-title"
                        variant="h5"
                        component="h2"
                        sx={{ color: '#000000' }}
                    >
                        {t('selectedAsset')}&nbsp;
                    </Typography>
                    <TruncatedText
                        text={assetName}
                        component="h2"
                        variant="h6"
                    />
                </Box>
                <SearchInput
                    id="fm-sensors-search-input"
                    label=""
                    placeholder={t('searchDevice')}
                    value={searchText}
                    onChange={onSearch}
                    onSelect={(obj: any) => onSelectSensor(obj)}
                    searchedList={freeSensors || []}
                />
                <Button
                    id="fm-sensors-button-submit"
                    sx={{
                        mt: 2,
                    }}
                    variant="contained"
                    fullWidth
                    onClick={onSubmitSensor}
                    disabled={disableSubmit}
                    title={disableSubmit ? t('submitDisabled') : t('submit')}
                >
                    {t('submit')}
                </Button>
            </Box>
        </Modal>
    )
}

export default SensorsDropDownModal
